import { afterNextRender, Directive, ElementRef, inject, NgZone, OnDestroy, output } from '@angular/core';

/**
 * Emits whether an element's content is taller than the box showing it.
 *
 * The reasoning panel used to decide whether to offer a "Show more" control from the
 * character count of the raw payload, and that does not survive contact with the content:
 * 500 characters of prose sit comfortably inside the clamp, while 500 characters holding a
 * preview table do not. So the control turned up on results that had nothing further to
 * show — pressing it changed the fade and nothing else — and stayed away from results that
 * were genuinely cut off.
 *
 * Measuring the box answers the question the control is actually asking. A ResizeObserver
 * keeps the answer current as a payload streams in and as the clamp itself is toggled,
 * which changes `clientHeight` under us.
 */
@Directive({ selector: '[besharpOverflows]', standalone: true })
export class OverflowsDirective implements OnDestroy {
  /** True while the content does not fit; re-emitted only when the answer changes. */
  readonly besharpOverflows = output<boolean>();

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly zone = inject(NgZone);
  private observer?: ResizeObserver;
  private last?: boolean;

  constructor() {
    // Measuring before the first paint would read a box that has not been laid out yet.
    afterNextRender(() => this.watch());
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private watch(): void {
    const el = this.host.nativeElement;
    this.measure(el);

    if (typeof ResizeObserver === 'undefined') return;
    // Outside Angular: this fires on every layout pass of a streaming response, and each
    // one would otherwise schedule a full change-detection run of the chat.
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.measure(el));
      this.observer.observe(el);
    });
  }

  private measure(el: HTMLElement): void {
    // One pixel of slack: sub-pixel line heights make scrollHeight round up past
    // clientHeight on content that visually fits, which would offer a control that opens
    // onto nothing.
    const overflows = el.scrollHeight > el.clientHeight + 1;
    if (overflows === this.last) return;

    this.last = overflows;
    this.zone.run(() => this.besharpOverflows.emit(overflows));
  }
}
